import { useState } from 'react';

import { Box, Collapse, ListItemButton, ListItemIcon, ListItemText, useTheme } from '@mui/material';

import VectorImage from '~components/media/vectorImage';
import { Dimens } from '~themes';

import menuRowSx from '../menuRowSx';
import { SideMenuDropdown } from '../types';
import { MenuLi, MenuSubList } from './menuPrimitives';
import PageRow from './pageRow';

interface Props {
	dropdown: SideMenuDropdown;
	initiallyOpen: boolean;
	/** Highlight the header like a selected row when a child page is active. */
	active: boolean;
}

/** A dropdown rendered inline (expanded menu): a header that toggles a sub-list. */
const DropdownInline = ({ dropdown, initiallyOpen, active }: Props) => {
	const { typography, palette, transitions } = useTheme();
	const [open, setOpen] = useState(initiallyOpen);

	return (
		<MenuLi block>
			<ListItemButton
				onClick={() => setOpen((prev) => !prev)}
				sx={menuRowSx({
					active: active && !open,
				})}
			>
				{dropdown.iconName && (
					<ListItemIcon sx={{ minWidth: Dimens.spacing2xl }}>
						<VectorImage name={dropdown.iconName} size={Dimens.spacing2xl} />
					</ListItemIcon>
				)}
				<ListItemText
					primary={dropdown.title}
					sx={{ ['& .MuiListItemText-primary']: { xs: typography.labelSm, xl: typography.label } }}
				/>
				<Box
					sx={{
						display: 'flex',
						transformOrigin: 'center',
						transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
						transition: transitions.create('transform', {
							duration: transitions.duration.shortest,
						}),
					}}
				>
					<VectorImage
						name='chevron/down'
						size={Dimens.spacingXl}
						color={palette.fgTertiary.main}
					/>
				</Box>
			</ListItemButton>
			<Collapse in={open} timeout='auto' unmountOnExit sx={{ width: '100%' }}>
				<MenuSubList
					sx={{
						paddingTop: { xs: Dimens.spacingXs, md: Dimens.spacingNone, xl: Dimens.spacingXs },
					}}
				>
					{dropdown.pages.map((page) => (
						<PageRow key={page.label} page={page} showLabel nested />
					))}
				</MenuSubList>
			</Collapse>
		</MenuLi>
	);
};

export default DropdownInline;
